/** Dev21 — Gallery. Masonry-style grid with hover reveal + lightbox. */

import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X } from 'lucide-react';
import { Section, SectionHeading } from '../components/primitives';
import { DEV21_EASE } from '../theme';
import type { Dev21Content, GalleryImage } from '../content';

const SPAN_CLASS: Record<GalleryImage['span'], string> = {
  tall: 'sm:row-span-2',
  wide: 'sm:col-span-2',
  normal: '',
};

export function GallerySection({
  content,
}: {
  content: Dev21Content['gallery'];
}) {
  const [active, setActive] = React.useState<GalleryImage | null>(null);

  return (
    <Section id="gallery" tone="cream">
      <SectionHeading label={content.label} title={content.title} />

      <div className="mt-12 grid auto-rows-[200px] grid-cols-2 gap-3 sm:auto-rows-[230px] sm:grid-cols-3 sm:gap-4 lg:auto-rows-[260px]">
        {content.images.map((image, i) => (
          <motion.button
            key={image.src}
            type="button"
            onClick={() => setActive(image)}
            initial={{ opacity: 0, scale: 0.92 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.55, delay: (i % 6) * 0.07, ease: DEV21_EASE }}
            className={`group relative overflow-hidden rounded-2xl border border-[var(--d21-border)] ${SPAN_CLASS[image.span]}`}
          >
            <img
              src={image.src}
              alt={image.caption ?? ''}
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_50%,rgba(20,15,10,0.78)_100%)] opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
            {image.caption ? (
              <span className="absolute bottom-3 left-3 translate-y-3 text-sm font-medium text-white opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                {image.caption}
              </span>
            ) : null}
          </motion.button>
        ))}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {active ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-5 backdrop-blur-sm"
          >
            <button
              type="button"
              aria-label="Close"
              className="absolute right-5 top-5 rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20"
            >
              <X size={22} />
            </button>
            <motion.img
              key={active.src}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.35, ease: DEV21_EASE }}
              src={active.src}
              alt={active.caption ?? ''}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[85vh] max-w-full rounded-2xl object-contain shadow-2xl"
            />
          </motion.div>
        ) : null}
      </AnimatePresence>
    </Section>
  );
}
